import { useEffect, useState, type ReactNode } from 'react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Check, ChevronRight } from 'lucide-react';
import type {
  BoardOverview,
  BoardMemberItem,
  BoardPersonMini,
  BoardStatus,
  TaskPriority,
} from '@hygge/shared';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/errors';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { useEmployees } from '@/hooks/use-employees';
import { useBoardActivity } from '@/hooks/use-board-overview';
import { useUpdateBoard, useBoardMembers, useAddBoardMembers, useRemoveBoardMember, useBoardPermission, useGrantableBoardRoles, useMayChangeBoardMember } from '@/hooks/use-boards';
import { STATUS_STYLE } from './board-status';
import { ActivityItemRow } from './board-activity-tab';

interface OverviewSidebarProps {
  data: BoardOverview;
  onSeeAllActivity: () => void;
  onOpenTask: (key: string) => void;
}

const BOARD_STATUS_ORDER: BoardStatus[] = [
  'planning',
  'active',
  'paused',
  'completed',
  'cancelled',
  'archived',
];

const PRIORITY_OPTIONS: { value: TaskPriority; label: string }[] = [
  { value: 'none', label: 'No priority' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

/** Shared trigger style for the editable property values (hover affordance). */
const editTriggerCls =
  'inline-flex max-w-full items-center gap-1.5 rounded px-1.5 py-0.5 -mr-1 text-xs text-foreground transition-colors hover:bg-muted cursor-pointer';

function personInitials(firstName: string, lastName: string): string {
  return `${firstName[0] ?? ''}${lastName[0] ?? ''}`.toUpperCase();
}

function labelInitials(label: string): string {
  const parts = label.trim().split(/\s+/);
  return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase() || '?';
}

/** Collapsible sidebar card. Default-open, no persistence (Phase 1). */
function SidebarCard({ title, children }: { title: string; children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border-b border-border">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-1.5 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground cursor-pointer"
      >
        <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-90')} />
        {title}
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

function PropertyRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1">
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      <div className="min-w-0 text-right text-xs text-foreground">{children}</div>
    </div>
  );
}

function MemberStack({ members }: { members: BoardMemberItem[] }) {
  if (members.length === 0) return <span className="text-muted-foreground">—</span>;
  const shown = members.slice(0, 5);
  const extra = members.length - shown.length;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="flex -space-x-1.5">
        {shown.map((m) => (
          <Avatar key={m.employeeId} className="h-5 w-5 ring-2 ring-background">
            {m.avatarUrl && <AvatarImage src={m.avatarUrl} />}
            <AvatarFallback className="text-[9px]">
              {personInitials(m.firstName, m.lastName)}
            </AvatarFallback>
          </Avatar>
        ))}
        {extra > 0 && (
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[9px] font-medium text-muted-foreground ring-2 ring-background">
            +{extra}
          </span>
        )}
      </span>
      <span className="text-muted-foreground tabular-nums">{members.length}</span>
    </span>
  );
}

// ─── Read-only property renderers (shared by editors + read-only users) ──────

function StatusValue({ status }: { status: BoardStatus }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn('h-2 w-2 rounded-full', STATUS_STYLE[status].dot)} />
      {STATUS_STYLE[status].label}
    </span>
  );
}

function PriorityValue({ priority }: { priority: TaskPriority }) {
  return priority === 'none' ? (
    <span className="text-muted-foreground">—</span>
  ) : (
    <span className="capitalize">{priority}</span>
  );
}

function LeadValue({ lead, name }: { lead: BoardPersonMini | null; name: string }) {
  if (!lead) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <Avatar className="h-5 w-5">
        {lead.avatarUrl && <AvatarImage src={lead.avatarUrl} />}
        <AvatarFallback className="text-[9px]">
          {personInitials(lead.firstName, lead.lastName)}
        </AvatarFallback>
      </Avatar>
      {name}
    </span>
  );
}

// ─── Inline editors (rendered only when this board allows board.settings) ────

function StatusEditor({ boardId, status }: { boardId: string; status: BoardStatus }) {
  const [open, setOpen] = useState(false);
  const updateBoard = useUpdateBoard();

  const select = (value: BoardStatus) => {
    setOpen(false);
    if (value !== status) {
      updateBoard.mutate(
        { id: boardId, status: value },
        { onError: (e) => toast.error(extractErrorMessage(e, 'Failed to update status')) },
      );
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger className={editTriggerCls}>
        <StatusValue status={status} />
      </PopoverTrigger>
      <PopoverContent align="end" className="w-44 p-1">
        {BOARD_STATUS_ORDER.map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => select(value)}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors hover:bg-muted cursor-pointer"
          >
            <span className={cn('h-2 w-2 rounded-full', STATUS_STYLE[value].dot)} />
            <span className="flex-1">{STATUS_STYLE[value].label}</span>
            {value === status && <Check className="h-3.5 w-3.5 text-hygge" />}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function PriorityEditor({ boardId, priority }: { boardId: string; priority: TaskPriority }) {
  const [open, setOpen] = useState(false);
  const updateBoard = useUpdateBoard();

  const select = (value: TaskPriority) => {
    setOpen(false);
    if (value !== priority) {
      updateBoard.mutate(
        { id: boardId, priority: value },
        { onError: (e) => toast.error(extractErrorMessage(e, 'Failed to update priority')) },
      );
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger className={editTriggerCls}>
        <PriorityValue priority={priority} />
      </PopoverTrigger>
      <PopoverContent align="end" className="w-44 p-1">
        {PRIORITY_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => select(opt.value)}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors hover:bg-muted cursor-pointer"
          >
            <span className="flex-1">{opt.label}</span>
            {opt.value === priority && <Check className="h-3.5 w-3.5 text-hygge" />}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function LeadEditor({
  boardId,
  lead,
  name,
}: {
  boardId: string;
  lead: BoardPersonMini | null;
  name: string;
}) {
  const [open, setOpen] = useState(false);
  const updateBoard = useUpdateBoard();
  const formatName = useEmployeeNameFormatter();
  // Lazy-load members only while the picker is open.
  const { data: members = [] } = useBoardMembers(open ? boardId : undefined);

  const select = (leadId: string | null) => {
    setOpen(false);
    if (leadId !== (lead?.id ?? null)) {
      updateBoard.mutate(
        { id: boardId, leadId },
        { onError: (e) => toast.error(extractErrorMessage(e, 'Failed to update lead')) },
      );
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger className={editTriggerCls}>
        <LeadValue lead={lead} name={name} />
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-1">
        <div className="max-h-64 overflow-y-auto">
          <button
            type="button"
            onClick={() => select(null)}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors hover:bg-muted cursor-pointer"
          >
            <span className="flex-1 text-muted-foreground">No lead</span>
            {!lead && <Check className="h-3.5 w-3.5 text-hygge" />}
          </button>
          {members.map((m) => (
            <button
              key={m.employeeId}
              type="button"
              onClick={() => select(m.employeeId)}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors hover:bg-muted cursor-pointer"
            >
              <Avatar className="h-5 w-5 shrink-0">
                {m.avatarUrl && <AvatarImage src={m.avatarUrl} />}
                <AvatarFallback className="text-[9px]">
                  {personInitials(m.firstName, m.lastName)}
                </AvatarFallback>
              </Avatar>
              <span className="flex-1 truncate">
                {formatName({ firstName: m.firstName, lastName: m.lastName })}
              </span>
              {m.employeeId === lead?.id && <Check className="h-3.5 w-3.5 shrink-0 text-hygge" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function TargetDateEditor({
  boardId,
  targetDate,
}: {
  boardId: string;
  targetDate: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const updateBoard = useUpdateBoard();

  useEffect(() => {
    if (open) setDraft(targetDate ? targetDate.slice(0, 10) : '');
  }, [open, targetDate]);

  const save = (value: string | null) => {
    setOpen(false);
    // Skip a redundant PATCH when the date didn't change (matches the other editors).
    if (value === (targetDate ? targetDate.slice(0, 10) : null)) return;
    updateBoard.mutate(
      { id: boardId, targetDate: value },
      { onError: (e) => toast.error(extractErrorMessage(e, 'Failed to update target date')) },
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger className={editTriggerCls}>
        {targetDate ? (
          format(new Date(targetDate), 'MMM d, yyyy')
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 space-y-2 p-2">
        <input
          type="date"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="h-8 w-full rounded-md border border-input bg-transparent px-2 text-xs outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        <div className="flex items-center justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-xs"
            disabled={!targetDate && !draft}
            onClick={() => save(null)}
          >
            Clear
          </Button>
          <Button type="button" size="sm" className="text-xs" onClick={() => save(draft || null)}>
            Save
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function MembersEditor({ boardId, members }: { boardId: string; members: BoardMemberItem[] }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger className={editTriggerCls}>
        <MemberStack members={members} />
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-0">
        <MembersChecklist boardId={boardId} />
      </PopoverContent>
    </Popover>
  );
}

/**
 * Rendered only inside an open Members popover, so the employee query fires
 * lazily. Toggles board membership via the existing member hooks (add/remove);
 * both invalidate the overview aggregate so the avatar stack refreshes.
 *
 * Ticks come from the full member list, not the overview's board rows: somebody
 * on the board through its project, or as its creator, is on it. Such a
 * row is shown ticked and fixed — ticking it again wrote a plain `member` row,
 * which outranks the project and quietly demoted an admin. A row is offered for
 * unticking only where the server would allow it: not above the caller, and not
 * the last owner.
 */
function MembersChecklist({ boardId }: { boardId: string }) {
  const [search, setSearch] = useState('');
  const formatName = useEmployeeNameFormatter();
  const addMembers = useAddBoardMembers(boardId);
  const removeMember = useRemoveBoardMember(boardId);
  // Until the list arrives every row would read as "not on the board", and
  // ticking one then wrote a `member` row over whatever role they held.
  const { data: roster = [], isSuccess: rosterLoaded } = useBoardMembers(boardId);
  const grantable = useGrantableBoardRoles(boardId);
  const mayChange = useMayChangeBoardMember(boardId);
  const { data: employeesData } = useEmployees({ search, limit: 20, status: 'active' });

  const entryOf = new Map(roster.map((m) => [m.employeeId, m]));
  const ownerCount = roster.filter((m) => m.effectiveRole === 'owner').length;
  const candidates = employeesData?.data ?? [];

  /** Why this row cannot be toggled, or null when it can. */
  const lockedBecause = (id: string): string | null => {
    if (!rosterLoaded) return 'loading';
    const entry = entryOf.get(id);
    if (!entry) return grantable.includes('member') ? null : 'cannot add';
    if (entry.role === null) return entry.source === 'project' ? 'via project' : entry.source;
    if (!mayChange(entry.role)) return entry.role;
    if (entry.effectiveRole === 'owner' && ownerCount === 1) return 'last owner';
    return null;
  };

  const toggle = (id: string, nextChecked: boolean) => {
    if (nextChecked) {
      addMembers.mutate({ employeeIds: [id] }, {
        onError: (e) => toast.error(extractErrorMessage(e, 'Failed to add member')),
      });
    } else {
      removeMember.mutate(id, {
        onError: (e) => toast.error(extractErrorMessage(e, 'Failed to remove member')),
      });
    }
  };

  return (
    <div>
      <div className="border-b border-border p-2">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search people…"
          className="h-8 text-xs"
        />
      </div>
      <div className="max-h-56 overflow-y-auto p-1">
        {candidates.length === 0 ? (
          <p className="px-2 py-4 text-center text-xs text-muted-foreground">No people found</p>
        ) : (
          candidates.map((e) => {
            const checked = entryOf.has(e.id);
            const locked = lockedBecause(e.id);
            return (
              <button
                key={e.id}
                type="button"
                disabled={locked !== null}
                onClick={() => toggle(e.id, !checked)}
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left transition-colors hover:bg-muted cursor-pointer disabled:cursor-default disabled:hover:bg-transparent"
              >
                <Checkbox checked={checked} className="pointer-events-none" />
                <Avatar className="h-5 w-5 shrink-0">
                  {e.avatarUrl && <AvatarImage src={e.avatarUrl} />}
                  <AvatarFallback className="text-[9px]">
                    {personInitials(e.firstName, e.lastName)}
                  </AvatarFallback>
                </Avatar>
                <span className="flex-1 truncate text-xs">
                  {formatName({ firstName: e.firstName, lastName: e.lastName })}
                </span>
                {locked && checked && (
                  <span className="shrink-0 text-[10px] text-muted-foreground">{locked}</span>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

export function OverviewSidebar({ data, onSeeAllActivity, onOpenTask }: OverviewSidebarProps) {
  const formatName = useEmployeeNameFormatter();
  // Two questions, because the server asks two. Status, priority, the target
  // date and the lead are board properties — the lead is a name and grants
  // nothing; members are member management.
  const canEdit = useBoardPermission(data.board.id, 'board.settings');
  const canManageMembers = useBoardPermission(data.board.id, 'member.manage');
  // Adding somebody grants them the `member` role, which is capped at the
  // caller's own rank — so holding the permission is not enough on its own.
  const grantable = useGrantableBoardRoles(data.board.id);
  const canAddMembers = canManageMembers && grantable.includes('member');
  const [progressTab, setProgressTab] = useState<'assignees' | 'labels'>('assignees');
  const { data: activity } = useBoardActivity(data.board.id);

  const { board, members, tags, progress } = data;
  const boardId = board.id;
  const team = board.departmentName ?? board.projectName;
  const sidebarSprints = data.sprints.filter(
    (s) => s.status !== 'completed' && s.status !== 'cancelled',
  );
  const progressEntries = progressTab === 'assignees' ? progress.byAssignee : progress.byLabel;
  const recentActivity = (activity ?? []).slice(0, 2);

  const lead: BoardPersonMini | null = board.lead;
  const leadName = lead
    ? formatName({ firstName: lead.firstName, lastName: lead.lastName })
    : '';

  return (
    <div>
      {/* 1. Properties */}
      <SidebarCard title="Properties">
        <div className="divide-y divide-border/40">
          <PropertyRow label="Status">
            {canEdit ? (
              <StatusEditor boardId={boardId} status={board.status} />
            ) : (
              <StatusValue status={board.status} />
            )}
          </PropertyRow>

          <PropertyRow label="Priority">
            {canEdit ? (
              <PriorityEditor boardId={boardId} priority={board.priority} />
            ) : (
              <PriorityValue priority={board.priority} />
            )}
          </PropertyRow>

          <PropertyRow label="Lead">
            {canEdit ? (
              <LeadEditor boardId={boardId} lead={lead} name={leadName} />
            ) : (
              <LeadValue lead={lead} name={leadName} />
            )}
          </PropertyRow>

          <PropertyRow label="Members">
            {canAddMembers ? (
              <MembersEditor boardId={boardId} members={members} />
            ) : (
              <MemberStack members={members} />
            )}
          </PropertyRow>

          <PropertyRow label="Target date">
            {canEdit ? (
              <TargetDateEditor boardId={boardId} targetDate={board.targetDate} />
            ) : board.targetDate ? (
              format(new Date(board.targetDate), 'MMM d, yyyy')
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </PropertyRow>

          <PropertyRow label="Team">
            {team ?? <span className="text-muted-foreground">—</span>}
          </PropertyRow>

          <PropertyRow label="Labels">
            {tags.length === 0 ? (
              <span className="text-muted-foreground">—</span>
            ) : (
              <div className="flex flex-wrap justify-end gap-1">
                {tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="rounded px-1.5 py-0.5 text-[10px] font-medium"
                    style={{ backgroundColor: `${tag.color}26`, color: tag.color }}
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            )}
          </PropertyRow>
        </div>
      </SidebarCard>

      {/* 2. Sprints */}
      <SidebarCard title="Sprints">
        {sidebarSprints.length === 0 ? (
          <p className="text-xs text-muted-foreground">No sprints</p>
        ) : (
          <div className="space-y-2.5">
            {sidebarSprints.map((sprint) => {
              const pct =
                sprint.totalPoints > 0
                  ? Math.round((sprint.completedPoints / sprint.totalPoints) * 100)
                  : 0;
              return (
                <div key={sprint.id} className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground">
                    {sprint.name}
                  </span>
                  <div className="h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-border">
                    <div
                      className="h-full rounded-full bg-hygge"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                    {sprint.completedPoints}/{sprint.totalPoints} pts
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </SidebarCard>

      {/* 3. Progress */}
      <SidebarCard title="Progress">
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg border border-border p-2.5">
            <div className="text-lg font-semibold tabular-nums text-foreground">
              {progress.scopePoints}
            </div>
            <div className="text-[11px] text-muted-foreground">Scope</div>
          </div>
          <div className="rounded-lg border border-border p-2.5">
            <div className="text-lg font-semibold tabular-nums text-foreground">
              {progress.completedPoints}
            </div>
            <div className="text-[11px] text-muted-foreground">Completed</div>
          </div>
        </div>

        <div className="mt-3 inline-flex rounded-md border border-border p-0.5">
          {(['assignees', 'labels'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setProgressTab(tab)}
              className={cn(
                'cursor-pointer rounded px-2 py-0.5 text-[11px] font-medium capitalize transition-colors',
                progressTab === tab
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="mt-3 space-y-1.5">
          {progressEntries.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              {progressTab === 'assignees' ? 'No assignees' : 'No labels'}
            </p>
          ) : (
            progressEntries.map((entry) => (
              <div key={entry.id} className="flex items-center gap-2">
                {progressTab === 'assignees' ? (
                  <Avatar className="h-5 w-5 shrink-0">
                    {entry.avatarUrl && <AvatarImage src={entry.avatarUrl} />}
                    <AvatarFallback className="text-[9px]">
                      {labelInitials(entry.label)}
                    </AvatarFallback>
                  </Avatar>
                ) : (
                  <span
                    className={cn(
                      'h-2.5 w-2.5 shrink-0 rounded-full',
                      !entry.color && 'bg-muted-foreground/40',
                    )}
                    style={entry.color ? { backgroundColor: entry.color } : undefined}
                  />
                )}
                <span className="min-w-0 flex-1 truncate text-xs text-foreground">
                  {entry.label}
                </span>
                <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                  {entry.count}
                </span>
              </div>
            ))
          )}
        </div>
      </SidebarCard>

      {/* 4. Activity */}
      <SidebarCard title="Activity">
        {recentActivity.length === 0 ? (
          <p className="text-xs text-muted-foreground">No activity yet.</p>
        ) : (
          <ul className="space-y-4">
            {recentActivity.map((item) => (
              <ActivityItemRow key={`${item.kind}-${item.id}`} item={item} onOpenTask={onOpenTask} />
            ))}
          </ul>
        )}
        <button
          type="button"
          onClick={onSeeAllActivity}
          className="mt-3 cursor-pointer text-xs font-medium text-hygge hover:underline"
        >
          See all
        </button>
      </SidebarCard>
    </div>
  );
}
